import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api';
import { useProfiles } from '../queries';
import TenderCard from '../components/TenderCard';
import { tenderRegionNames } from '../data/nuts';
import '../animations';
import { LS_KEYS } from '../constants';
import { computeCompleteness } from '../hooks/useProfileCompleteness';
import { useWindowWidth } from '../hooks/useWindowWidth';
import { useImpersonation } from '../context/ImpersonationContext';
import { useToast } from '../components/Toast';

const PAGE_SIZE = 10;

const VERDICT_LABELS  = { great: 'Great fit', good: 'Good fit', maybe: 'Maybe', cosine: 'Unreviewed' };
const VALUE_LABELS    = { under_50k: 'Under €50k', '50k_500k': '€50k – €500k', over_500k: 'Over €500k' };
const DEADLINE_LABELS = { week: 'This week', month: 'This month', quarter: 'Next 3 months' };
const SORT_OPTIONS    = [
  { value: 'score',      label: 'Best match' },
  { value: 'deadline',   label: 'Soonest deadline' },
  { value: 'value_desc', label: 'Highest value €' },
  { value: 'value_asc',  label: 'Lowest value €' },
];

const s = {
  page: { maxWidth: 1100, margin: '32px auto', padding: '0 24px 60px' },
  header: { marginBottom: 20 },
  heading: { fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  subheading: { fontSize: 15, color: '#64748b' },
  loadingBox: {
    textAlign: 'center', padding: '80px 20px',
    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
  },
  spinner: {
    width: 40, height: 40,
    border: '3px solid #e2e8f0', borderTop: '3px solid #2563eb',
    borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
  },
  loadingText: { fontSize: 15, color: '#64748b' },
  loadingHint: { fontSize: 13, color: '#94a3b8', marginTop: 8 },
  emptyBox: {
    textAlign: 'center', padding: '60px 20px',
    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
  },
  emptyTitle: { fontSize: 18, fontWeight: 600, color: '#1e293b', marginBottom: 10 },
  emptyText: { fontSize: 14, color: '#64748b', marginBottom: 20 },
  profileLink: {
    display: 'inline-block', background: '#1e40af', color: '#fff', fontWeight: 600,
    fontSize: 14, padding: '10px 22px', borderRadius: 8, textDecoration: 'none',
  },
  error: { background: '#fef2f2', color: '#b91c1c', padding: '14px 18px', borderRadius: 10, fontSize: 14, marginBottom: 20 },
  metaRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 12, gap: 10, flexWrap: 'wrap',
  },
  metaLeft: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  count: { fontSize: 14, color: '#64748b' },
  select: {
    padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 8,
    fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer',
  },
  refreshBtn: {
    background: '#fff', border: '1px solid #e2e8f0', color: '#374151',
    fontWeight: 500, fontSize: 13, padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
  },
  loadMoreBtn: {
    display: 'block', width: '100%', textAlign: 'center',
    background: '#fff', border: '1px solid #e2e8f0', color: '#374151',
    fontWeight: 500, fontSize: 14, padding: '12px', borderRadius: 10,
    cursor: 'pointer', marginTop: 8,
  },
  pageInfo: { textAlign: 'center', fontSize: 13, color: '#94a3b8', marginTop: 12 },
  completenessWarning: {
    background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10,
    padding: '11px 16px', marginBottom: 20,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    fontSize: 14, color: '#92400e',
  },
  profilePill: (active) => ({
    padding: '5px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
    border: active ? '2px solid #1e40af' : '1px solid #d1d5db',
    background: active ? '#eff6ff' : '#fff',
    color: active ? '#1e40af' : '#374151', fontWeight: active ? 600 : 400,
  }),
  searchInput: {
    padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10,
    fontSize: 14, color: '#374151', background: '#fff', outline: 'none', width: '100%',
    boxSizing: 'border-box', marginBottom: 14,
  },
  // Filter bar
  filterSection: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: '12px 18px', marginBottom: 10,
  },
  filterRow: {
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  filterRowLeft: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  filterLabel: {
    fontSize: 11, fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap',
  },
  filterDivider: { width: 1, height: 20, background: '#e2e8f0', margin: '0 4px', flexShrink: 0 },
  scoreRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    borderTop: '1px solid #f1f5f9', marginTop: 10, paddingTop: 10,
  },
  scoreLabel: { fontSize: 12, color: '#94a3b8', fontWeight: 500, whiteSpace: 'nowrap' },
  scoreInput: {
    width: 60, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6,
    fontSize: 13, color: '#374151', background: '#f8fafc', outline: 'none',
    textAlign: 'center',
  },
  filterBtnWrap: { position: 'relative' },
  // Filter pills — blue when active
  filterBtn: (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '6px 13px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
    border: active ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
    background: active ? '#eff6ff' : '#f8fafc',
    color: active ? '#1d4ed8' : '#374151',
    fontWeight: active ? 600 : 400, whiteSpace: 'nowrap',
  }),
  savedBtn: (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 13px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
    border: active ? '1.5px solid #e11d48' : '1px solid #e2e8f0',
    background: active ? '#fff1f2' : '#f8fafc',
    color: active ? '#e11d48' : '#374151',
    fontWeight: active ? 600 : 400, whiteSpace: 'nowrap',
  }),
  // Sort button — visually distinct: rectangular, slate background
  sortBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '6px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
    border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#334155',
    fontWeight: 500, whiteSpace: 'nowrap',
  },
  dropdownPanel: {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0,
    background: '#fff', border: '1px solid #e2e8f0',
    borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
    zIndex: 200, minWidth: 180, padding: '4px 0', overflow: 'hidden',
  },
  dropdownItem: (selected) => ({
    padding: '8px 16px', fontSize: 13, cursor: 'pointer',
    background: selected ? '#eff6ff' : 'transparent',
    color: selected ? '#1d4ed8' : '#374151',
    fontWeight: selected ? 600 : 400,
  }),
  chipsRow: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 8px 4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500,
    background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
  },
  chipRemove: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#93c5fd', fontSize: 16, lineHeight: 1, padding: '0 2px',
    display: 'flex', alignItems: 'center',
  },
  clearAllBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 12, color: '#94a3b8', textDecoration: 'underline', padding: '4px 2px',
  },
};

function ChevronDown() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function SortIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18M7 12h10M11 18h2" />
    </svg>
  );
}

function StatsStrip({ matches }) {
  const width    = useWindowWidth();
  const isMobile = width < 600;

  const total    = matches.length;
  const great    = matches.filter((m) => m.verdict === 'great').length;
  const avgScore = total > 0 ? Math.round(matches.reduce((sum, m) => sum + m.score, 0) / total) : 0;
  const countryCount = new Set(matches.map((m) => m.tender?.country).filter(Boolean)).size;

  const stats = [
    { label: 'Matches found', value: total },
    { label: 'Great fits',    value: great, accent: great > 0 },
    { label: 'Avg fit score', value: avgScore, unit: '/ 100' },
    { label: 'Countries',     value: countryCount },
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: 12, marginBottom: 24,
    }}>
      {stats.map((stat) => (
        <div key={stat.label} style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#94a3b8', marginBottom: 8 }}>
            {stat.label}
          </div>
          <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: stat.accent ? '#16a34a' : '#0f172a' }}>
            {stat.value}
            {stat.unit && <span style={{ fontSize: 13, fontWeight: 400, color: '#94a3b8', marginLeft: 5 }}>{stat.unit}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

const CPV_DIV = {
  '03':'Agriculture','09':'Petroleum','14':'Mining','15':'Food & Beverages',
  '22':'Printed Matter','24':'Chemical Products','30':'Office Equipment',
  '31':'Electrical Equipment','32':'Communications','33':'Medical Equipment',
  '34':'Transport Equipment','35':'Security Equipment','38':'Lab Equipment',
  '39':'Furniture','42':'Industrial Machinery','44':'Construction Materials',
  '45':'Construction Works','48':'Software','50':'Repair & Maintenance',
  '55':'Hospitality','60':'Transport Services','63':'Logistics',
  '64':'Postal Services','65':'Utilities','66':'Financial Services',
  '70':'Real Estate','71':'Architecture & Engineering','72':'IT Services',
  '73':'R&D','75':'Public Administration','79':'Business Services',
  '80':'Education','85':'Healthcare','90':'Environmental Services',
  '92':'Cultural & Recreational','98':'Community Services',
};
function matchCpvLabel(code) { return CPV_DIV[code?.slice(0, 2)] || code; }

export default function Dashboard() {
  const { impersonating } = useImpersonation();
  const showToast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [sortBy, setSortBy]                 = useState('score');
  const [filterCountry, setFilterCountry]   = useState('');
  const [filterRegion, setFilterRegion]     = useState('');
  const [filterVerdicts, setFilterVerdicts] = useState([]);
  const [filterSectors, setFilterSectors]   = useState([]);
  const [filterScoreMin, setFilterScoreMin] = useState('');
  const [filterScoreMax, setFilterScoreMax] = useState('');
  const [filterValue, setFilterValue]       = useState('');
  const [filterDeadline, setFilterDeadline] = useState('');
  const [filterBookmarked, setFilterBookmarked] = useState(false);
  const [page, setPage]                     = useState(1);
  const [searchQuery, setSearchQuery]       = useState('');
  const [bookmarked, setBookmarked]         = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('tm_bookmarked') || '[]')); }
    catch { return new Set(); }
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const filterBarRef = useRef(null);

  // --- Cached server reads (TanStack Query) ---------------------------------
  // Keys are namespaced by impersonation id so impersonated data never collides
  // with the admin's own cached data. Cached rows render instantly on return;
  // a background refetch keeps them fresh (incl. on tab refocus).
  const profilesQuery = useProfiles();
  const profiles = useMemo(() => profilesQuery.data ?? [], [profilesQuery.data]);

  // Resolve the active profile *synchronously* so cached data paints on the
  // first render with no spinner flash. Explicit dropdown choice wins;
  // otherwise honour ?profile_id=, else fall back to "all".
  const activeProfileId = useMemo(() => {
    if (selectedProfileId !== null) return selectedProfileId;
    const urlId = searchParams.get('profile_id');
    if (urlId && profiles.find((p) => p.id === urlId)) return urlId;
    return 'all';
  }, [selectedProfileId, searchParams, profiles]);

  const matchesQuery = useQuery({
    queryKey: ['matches', activeProfileId, impersonating?.id],
    queryFn: () => {
      const url = activeProfileId !== 'all'
        ? `/matches?profile_id=${activeProfileId}`
        : '/matches';
      return apiFetch(url);
    },
    enabled: !profilesQuery.isLoading, // know the right id before fetching
  });
  const matches = useMemo(() => matchesQuery.data ?? [], [matchesQuery.data]);

  // Spinner only when we genuinely have no data yet — never on a cached return
  // or a background revalidation (that would reintroduce the flicker).
  const loading = profilesQuery.isLoading || matchesQuery.isLoading;
  const error = matchesQuery.error
    ? (matchesQuery.error.message?.includes('complete your company profile')
        ? 'profile_needed'
        : matchesQuery.error.message)
    : '';

  // Currently-selected profile object (for the completeness widget).
  const profile = useMemo(() => {
    if (activeProfileId === 'all') return null;
    return profiles.find((p) => p.id === activeProfileId) || null;
  }, [activeProfileId, profiles]);

  // Drop any explicit selection when impersonation changes so it re-resolves
  // against the impersonated user's profiles.
  useEffect(() => { setSelectedProfileId(null); }, [impersonating?.id]);

  // Reset pagination whenever the active profile changes.
  useEffect(() => { setPage(1); }, [activeProfileId]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (filterBarRef.current && !filterBarRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Returning from a successful Stripe checkout (?payment=success).
  useEffect(() => {
    if (searchParams.get('payment') !== 'success') return;
    showToast('Payment successful — your plan is now active. 🎉', 'success', 6000);
    // Refresh subscription state app-wide. The webhook may land a beat after the
    // redirect, so ask once now and again shortly after.
    window.dispatchEvent(new Event('tm:subscription-updated'));
    const t = setTimeout(() => window.dispatchEvent(new Event('tm:subscription-updated')), 3000);
    // Strip the param so a refresh doesn't replay the toast.
    searchParams.delete('payment');
    setSearchParams(searchParams, { replace: true });
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  function toggleDropdown(name) {
    setOpenDropdown((prev) => (prev === name ? null : name));
  }

  function clearFilters() {
    setFilterCountry('');
    setFilterRegion('');
    setFilterVerdicts([]);
    setFilterSectors([]);
    setFilterScoreMin('');
    setFilterScoreMax('');
    setFilterValue('');
    setFilterDeadline('');
    setFilterBookmarked(false);
    setPage(1);
  }

  function toggleBookmark(tenderId) {
    setBookmarked((prev) => {
      const next = new Set(prev);
      if (next.has(tenderId)) next.delete(tenderId); else next.add(tenderId);
      localStorage.setItem('tm_bookmarked', JSON.stringify([...next]));
      return next;
    });
  }

  // Selecting a profile just updates state — activeProfileId recomputes and the
  // matches query refetches automatically when its key changes.
  function selectProfile(profileId) {
    setSelectedProfileId(profileId);
    if (profileId && profileId !== 'all') {
      localStorage.setItem(LS_KEYS.activeProfileId, profileId);
    }
  }

  const { score: profileScore } = computeCompleteness(profile);

  const profileMap = useMemo(() =>
    Object.fromEntries(profiles.map((p) => [p.id, p.profile_name])),
  [profiles]);

  const countries = useMemo(() => {
    const set = new Set(matches.map((m) => m.tender.country).filter(Boolean));
    return Array.from(set).sort();
  }, [matches]);

  // Region names per tender, computed once (reused by the dropdown list and the
  // filter). A multi-region tender contributes each of its regions, so filtering
  // by a single region still surfaces it.
  const matchRegions = useMemo(() => {
    const map = new Map();
    matches.forEach((m) => map.set(m.tender.id, tenderRegionNames(m.tender)));
    return map;
  }, [matches]);

  const regions = useMemo(() => {
    const set = new Set();
    matchRegions.forEach((names) => names.forEach((n) => set.add(n)));
    return Array.from(set).sort();
  }, [matchRegions]);

  const sectorLabels = useMemo(() => {
    const seen = new Set();
    matches.forEach((m) => (m.tender.cpv_codes || []).forEach((c) => seen.add(matchCpvLabel(c))));
    return Array.from(seen).sort();
  }, [matches]);

  const filtered = useMemo(() => {
    let result = matches;

    if (filterBookmarked) result = result.filter((m) => bookmarked.has(m.tender.id));

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((m) =>
        (m.tender.title || '').toLowerCase().includes(q) ||
        (m.reasoning || '').toLowerCase().includes(q)
      );
    }

    if (filterCountry) result = result.filter((m) => m.tender.country === filterCountry);

    if (filterRegion) result = result.filter((m) => (matchRegions.get(m.tender.id) || []).includes(filterRegion));

    if (filterVerdicts.length > 0) {
      result = result.filter((m) =>
        filterVerdicts.some((v) => v === 'cosine' ? m.source === 'cosine' : m.verdict === v)
      );
    }

    if (filterSectors.length > 0) {
      result = result.filter((m) =>
        (m.tender.cpv_codes || []).some((c) => filterSectors.includes(matchCpvLabel(c)))
      );
    }

    if (filterScoreMin !== '') result = result.filter((m) => m.score >= Number(filterScoreMin));
    if (filterScoreMax !== '') result = result.filter((m) => m.score <= Number(filterScoreMax));

    if (filterValue === 'under_50k') {
      result = result.filter((m) => m.tender.contract_value != null && m.tender.contract_value < 50000);
    } else if (filterValue === '50k_500k') {
      result = result.filter((m) => m.tender.contract_value != null && m.tender.contract_value >= 50000 && m.tender.contract_value <= 500000);
    } else if (filterValue === 'over_500k') {
      result = result.filter((m) => m.tender.contract_value != null && m.tender.contract_value > 500000);
    }

    if (filterDeadline) {
      const now = Date.now();
      const days = filterDeadline === 'week' ? 7 : filterDeadline === 'month' ? 30 : 90;
      const cutoff = now + days * 86400000;
      result = result.filter((m) => {
        if (!m.tender.deadline) return false;
        const d = new Date(m.tender.deadline).getTime();
        return d >= now && d <= cutoff;
      });
    }

    if (sortBy === 'value_desc') {
      result = [...result].sort((a, b) => (b.tender.contract_value || 0) - (a.tender.contract_value || 0));
    } else if (sortBy === 'value_asc') {
      result = [...result].sort((a, b) => (a.tender.contract_value || 0) - (b.tender.contract_value || 0));
    } else if (sortBy === 'deadline') {
      result = [...result].sort((a, b) => {
        const da = a.tender.deadline ? new Date(a.tender.deadline).getTime() : Infinity;
        const db = b.tender.deadline ? new Date(b.tender.deadline).getTime() : Infinity;
        return da - db;
      });
    } else {
      result = [...result].sort((a, b) => b.score - a.score);
    }

    return result;
  }, [matches, matchRegions, filterCountry, filterRegion, filterVerdicts, filterSectors, filterBookmarked, filterScoreMin, filterScoreMax, filterValue, filterDeadline, searchQuery, sortBy, bookmarked]);

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < filtered.length;
  const hasActiveFilters = filterCountry || filterRegion || filterVerdicts.length || filterSectors.length || filterScoreMin || filterScoreMax || filterValue || filterDeadline || filterBookmarked;
  const currentSortLabel = SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? 'Best match';

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.header}><h1 style={s.heading}>Your Tender Matches</h1></div>
        <div style={s.loadingBox}>
          <div style={s.spinner} />
          <div style={s.loadingText}>Finding your best matches</div>
          <div style={s.loadingHint}>Loading your matches — AI verdicts appear first, followed by cosine-scored candidates.</div>
        </div>
      </div>
    );
  }

  if (error === 'profile_needed') {
    return (
      <div style={s.page}>
        <div style={s.header}><h1 style={s.heading}>Your Tender Matches</h1></div>
        <div style={s.emptyBox}>
          <div style={s.emptyTitle}>Complete your profile first</div>
          <div style={s.emptyText}>We need to know about your company before we can find matching tenders. It only takes 2 minutes.</div>
          <Link to="/profiles/new" style={s.profileLink}>Set up profile</Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page}>
        <div style={s.header}><h1 style={s.heading}>Your Tender Matches</h1></div>
        <div style={s.error}>Error: {error}</div>
        <button style={s.refreshBtn} onClick={() => matchesQuery.refetch()}>Try again</button>
      </div>
    );
  }

  // Full-screen empty state only for the genuine "nothing anywhere" case (the
  // All-profiles view with zero matches). When a SPECIFIC profile is selected
  // and just happens to have no matches, we fall through to the normal overview
  // so the profile selector + filters stay put and show an inline empty message
  // (selecting an empty profile must not replace the whole screen).
  if (matches.length === 0 && activeProfileId === 'all') {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <h1 style={s.heading}>Your Tender Matches</h1>
          <p style={s.subheading}>No matches found yet</p>
        </div>
        <div style={s.emptyBox}>
          <div style={s.emptyTitle}>No tenders in the database yet</div>
          <div style={s.emptyText}>
            The tender database may still be loading. Check back in a few minutes,
            or update your profile to broaden your search.
          </div>
          <Link to="/profiles" style={s.profileLink}>Manage profiles</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.heading}>{impersonating ? `${impersonating.email}'s Matches` : 'Your Tender Matches'}</h1>
        <p style={s.subheading}>AI-matched EU tenders ranked by relevance to your company</p>
      </div>

      <StatsStrip matches={matches} />

      {!impersonating && profile && profileScore < 50 && (
        <div style={s.completenessWarning}>
          <span>Your profile is only {profileScore}% complete — matches may be less accurate.</span>
          <Link
            to={activeProfileId !== 'all' ? `/profiles/${activeProfileId}/edit` : '/profiles'}
            style={{ color: '#b45309', fontWeight: 600, textDecoration: 'underline', whiteSpace: 'nowrap' }}
          >
            Improve profile
          </Link>
        </div>
      )}

      <input
        type="text"
        placeholder="Search by title or match reason..."
        style={s.searchInput}
        value={searchQuery}
        onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
      />

      {/* Meta row */}
      <div style={s.metaRow}>
        <div style={s.metaLeft}>
          <span style={s.count}>
            {filtered.length} {filtered.length !== 1 ? 'matches' : 'match'}
            {filtered.length !== matches.length ? ' (of ' + matches.length + ')' : ''}
          </span>
          {profiles.length > 1 && (
            <select style={s.select} value={activeProfileId} onChange={(e) => selectProfile(e.target.value)}>
              <option value="all">All profiles</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.profile_name}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={s.scoreLabel}>Score</span>
          <input
            type="number" min="0" max="100" placeholder="Min"
            style={s.scoreInput}
            value={filterScoreMin}
            onChange={(e) => { setFilterScoreMin(e.target.value); setPage(1); }}
          />
          <span style={{ fontSize: 12, color: '#cbd5e1' }}>–</span>
          <input
            type="number" min="0" max="100" placeholder="Max"
            style={s.scoreInput}
            value={filterScoreMax}
            onChange={(e) => { setFilterScoreMax(e.target.value); setPage(1); }}
          />
          <span style={{ fontSize: 12, color: '#94a3b8' }}>/ 100</span>
          <button style={s.refreshBtn} onClick={() => matchesQuery.refetch()}>Refresh</button>
        </div>
      </div>

      {/* Filter + Sort bar */}
      <div style={s.filterSection}>
        <div ref={filterBarRef} style={s.filterRow}>

          {/* Left: filter pills */}
          <div style={s.filterRowLeft}>
            <span style={s.filterLabel}>Filter</span>
            <div style={s.filterDivider} />

            {/* Country */}
            {countries.length > 1 && (
              <div style={s.filterBtnWrap}>
                <button style={s.filterBtn(!!filterCountry)} onClick={() => toggleDropdown('country')}>
                  {filterCountry || 'Country'} <ChevronDown />
                </button>
                {openDropdown === 'country' && (
                  <div style={s.dropdownPanel}>
                    <div style={s.dropdownItem(!filterCountry)} onClick={() => { setFilterCountry(''); setPage(1); setOpenDropdown(null); }}>All countries</div>
                    {countries.map((c) => (
                      <div key={c} style={s.dropdownItem(filterCountry === c)} onClick={() => { setFilterCountry(c); setPage(1); setOpenDropdown(null); }}>{c}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Region */}
            {regions.length > 1 && (
              <div style={s.filterBtnWrap}>
                <button style={s.filterBtn(!!filterRegion)} onClick={() => toggleDropdown('region')}>
                  {filterRegion || 'Region'} <ChevronDown />
                </button>
                {openDropdown === 'region' && (
                  <div style={s.dropdownPanel}>
                    <div style={s.dropdownItem(!filterRegion)} onClick={() => { setFilterRegion(''); setPage(1); setOpenDropdown(null); }}>All regions</div>
                    {regions.map((r) => (
                      <div key={r} style={s.dropdownItem(filterRegion === r)} onClick={() => { setFilterRegion(r); setPage(1); setOpenDropdown(null); }}>{r}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Verdict — multi-select */}
            <div style={s.filterBtnWrap}>
              <button style={s.filterBtn(filterVerdicts.length > 0)} onClick={() => toggleDropdown('verdict')}>
                {filterVerdicts.length === 0 ? 'Verdict' : filterVerdicts.length === 1 ? VERDICT_LABELS[filterVerdicts[0]] : `Verdict: ${filterVerdicts.length}`}
                <ChevronDown />
              </button>
              {openDropdown === 'verdict' && (
                <div style={s.dropdownPanel}>
                  {[['great', 'Great fit'], ['good', 'Good fit'], ['maybe', 'Maybe'], ['cosine', 'Unreviewed']].map(([val, label]) => {
                    const active = filterVerdicts.includes(val);
                    return (
                      <div
                        key={val}
                        style={{ ...s.dropdownItem(active), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                        onClick={() => { setFilterVerdicts((prev) => active ? prev.filter((v) => v !== val) : [...prev, val]); setPage(1); }}
                      >
                        {label}
                        {active && <span style={{ color: '#2563eb', fontSize: 12, fontWeight: 700 }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sector — multi-select */}
            {sectorLabels.length > 1 && (
              <div style={s.filterBtnWrap}>
                <button style={s.filterBtn(filterSectors.length > 0)} onClick={() => toggleDropdown('sector')}>
                  {filterSectors.length === 0 ? 'Sector' : filterSectors.length === 1 ? filterSectors[0] : `Sector: ${filterSectors.length}`}
                  <ChevronDown />
                </button>
                {openDropdown === 'sector' && (
                  <div style={{ ...s.dropdownPanel, maxHeight: 240, overflowY: 'auto' }}>
                    {sectorLabels.map((l) => {
                      const active = filterSectors.includes(l);
                      return (
                        <div
                          key={l}
                          style={{ ...s.dropdownItem(active), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                          onClick={() => { setFilterSectors((prev) => active ? prev.filter((s) => s !== l) : [...prev, l]); setPage(1); }}
                        >
                          {l}
                          {active && <span style={{ color: '#2563eb', fontSize: 12, fontWeight: 700 }}>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Contract value */}
            <div style={s.filterBtnWrap}>
              <button style={s.filterBtn(!!filterValue)} onClick={() => toggleDropdown('value')}>
                {filterValue ? VALUE_LABELS[filterValue] : 'Value'} <ChevronDown />
              </button>
              {openDropdown === 'value' && (
                <div style={s.dropdownPanel}>
                  {[['', 'Any value'], ['under_50k', 'Under €50k'], ['50k_500k', '€50k – €500k'], ['over_500k', 'Over €500k']].map(([val, label]) => (
                    <div key={val || 'any'} style={s.dropdownItem(filterValue === val)} onClick={() => { setFilterValue(val); setPage(1); setOpenDropdown(null); }}>{label}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Deadline */}
            <div style={s.filterBtnWrap}>
              <button style={s.filterBtn(!!filterDeadline)} onClick={() => toggleDropdown('deadline')}>
                {filterDeadline ? DEADLINE_LABELS[filterDeadline] : 'Deadline'} <ChevronDown />
              </button>
              {openDropdown === 'deadline' && (
                <div style={s.dropdownPanel}>
                  {[['', 'Any deadline'], ['week', 'This week'], ['month', 'This month'], ['quarter', 'Next 3 months']].map(([val, label]) => (
                    <div key={val || 'any'} style={s.dropdownItem(filterDeadline === val)} onClick={() => { setFilterDeadline(val); setPage(1); setOpenDropdown(null); }}>{label}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Saved */}
            <button style={s.savedBtn(filterBookmarked)} onClick={() => { setFilterBookmarked((v) => !v); setPage(1); }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill={filterBookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              Saved
            </button>
          </div>

          {/* Right: sort button — distinct rectangular style */}
          <div style={{ ...s.filterBtnWrap, marginLeft: 'auto' }}>
            <button style={s.sortBtn} onClick={() => toggleDropdown('sort')}>
              <SortIcon />
              {currentSortLabel}
              <ChevronDown />
            </button>
            {openDropdown === 'sort' && (
              <div style={{ ...s.dropdownPanel, left: 'auto', right: 0 }}>
                {SORT_OPTIONS.map(({ value, label }) => (
                  <div
                    key={value}
                    style={s.dropdownItem(sortBy === value)}
                    onClick={() => { setSortBy(value); setPage(1); setOpenDropdown(null); }}
                  >
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Active filter chips */}
      {hasActiveFilters && (
        <div style={s.chipsRow}>
          {filterCountry && <span style={s.chip}>Country: {filterCountry}<button style={s.chipRemove} onClick={() => { setFilterCountry(''); setPage(1); }}>×</button></span>}
          {filterRegion && <span style={s.chip}>Region: {filterRegion}<button style={s.chipRemove} onClick={() => { setFilterRegion(''); setPage(1); }}>×</button></span>}
          {filterVerdicts.map((v) => (
            <span key={v} style={s.chip}>{VERDICT_LABELS[v]}<button style={s.chipRemove} onClick={() => { setFilterVerdicts((prev) => prev.filter((x) => x !== v)); setPage(1); }}>×</button></span>
          ))}
          {filterSectors.map((sec) => (
            <span key={sec} style={s.chip}>{sec}<button style={s.chipRemove} onClick={() => { setFilterSectors((prev) => prev.filter((x) => x !== sec)); setPage(1); }}>×</button></span>
          ))}
          {(filterScoreMin || filterScoreMax) && (
            <span style={s.chip}>
              Score: {filterScoreMin || '0'} – {filterScoreMax || '100'}
              <button style={s.chipRemove} onClick={() => { setFilterScoreMin(''); setFilterScoreMax(''); setPage(1); }}>×</button>
            </span>
          )}
          {filterValue    && <span style={s.chip}>{VALUE_LABELS[filterValue]}<button style={s.chipRemove} onClick={() => { setFilterValue('');    setPage(1); }}>×</button></span>}
          {filterDeadline && <span style={s.chip}>{DEADLINE_LABELS[filterDeadline]}<button style={s.chipRemove} onClick={() => { setFilterDeadline(''); setPage(1); }}>×</button></span>}
          {filterBookmarked && <span style={s.chip}>Saved only<button style={s.chipRemove} onClick={() => { setFilterBookmarked(false); setPage(1); }}>×</button></span>}
          <button style={s.clearAllBtn} onClick={clearFilters}>Clear all</button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={s.emptyBox}>
          {matches.length === 0 ? (
            <>
              <div style={s.emptyTitle}>This profile has no matches yet</div>
              <div style={s.emptyText}>
                Nothing has matched this profile yet. Try broadening its regions, countries or
                CPV areas, or check back after the next tender run.
              </div>
            </>
          ) : (
            <>
              <div style={s.emptyTitle}>No matches for these filters</div>
              <div style={s.emptyText}>Try removing some filters.</div>
            </>
          )}
        </div>
      ) : (
        <div>
          {visible.map((m) => (
            <TenderCard
              key={m.tender.id}
              tender={m.tender}
              score={m.score}
              verdict={m.verdict}
              reasoning={m.reasoning}
              source={m.source}
              isBookmarked={bookmarked.has(m.tender.id)}
              onToggleBookmark={toggleBookmark}
              profileName={profiles.length > 1 ? profileMap[m.profile_id] : undefined}
              cpvMatch={m.cpv_match}
            />
          ))}
          {hasMore && (
            <button style={s.loadMoreBtn} onClick={() => setPage((p) => p + 1)}>
              Load more ({filtered.length - visible.length} remaining)
            </button>
          )}
          {!hasMore && filtered.length > PAGE_SIZE && (
            <div style={s.pageInfo}>All {filtered.length} matches shown</div>
          )}
        </div>
      )}
    </div>
  );
}
